import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { auth as fbAuth } from '../../firebase/config';
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    RecaptchaVerifier,
    signInWithPhoneNumber
} from 'firebase/auth';
import { FiPhone, FiLock, FiMail, FiArrowLeft, FiEye, FiEyeOff } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Auth = ({ onAuthSuccess }) => {
    const [mode, setMode] = useState('email-login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [cooldown, setCooldown] = useState(0);
    const [recaptchaReady, setRecaptchaReady] = useState(false);

    const recaptchaContainerRef = useRef(null);
    const recaptchaInitialized = useRef(false);

    const [showPassword, setShowPassword] = useState(false);

    // Cooldown timer
    useEffect(() => {
        if (cooldown > 0) {
            const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [cooldown]);

    // Initialize reCAPTCHA when phone mode is selected
    const initRecaptcha = useCallback(() => {
        if (recaptchaInitialized.current || !recaptchaContainerRef.current) return;

        try {
            window.recaptchaVerifier = new RecaptchaVerifier(fbAuth, recaptchaContainerRef.current, {
                size: 'normal',
                callback: () => {
                    console.log('reCAPTCHA solved');
                    setRecaptchaReady(true);
                },
                'expired-callback': () => {
                    console.log('reCAPTCHA expired');
                    setRecaptchaReady(false);
                    setError('Verification expired. Please solve again.');
                }
            });

            window.recaptchaVerifier.render().then(() => {
                recaptchaInitialized.current = true;
                console.log('reCAPTCHA rendered');
            });
        } catch (err) {
            console.error('reCAPTCHA init error:', err);
        }
    }, []);

    // Initialize reCAPTCHA when switching to phone mode
    useEffect(() => {
        if (mode === 'phone' && !confirmationResult) {
            // Small delay to ensure DOM is ready
            const timer = setTimeout(() => {
                initRecaptcha();
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [mode, confirmationResult, initRecaptcha]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (window.recaptchaVerifier) {
                try {
                    window.recaptchaVerifier.clear();
                } catch (e) { }
                window.recaptchaVerifier = null;
            }
            recaptchaInitialized.current = false;
        };
    }, []);

    const handleSync = async (fbUser) => {
        try {
            const sessionId = localStorage.getItem('sessionId');
            const token = await fbUser.getIdToken();

            console.log('[AUTH] Syncing user:', {
                email: fbUser.email,
                phone: fbUser.phoneNumber,
                uid: fbUser.uid
            });

            const res = await axios.post(`${API_URL}/users/sync`, {
                email: fbUser.email || null,
                phoneNumber: fbUser.phoneNumber || null,
                firebaseUid: fbUser.uid,
                sessionId: sessionId
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            console.log('[AUTH] Sync successful:', res.data.user);

            localStorage.setItem('token', token);
            onAuthSuccess(res.data.user);
        } catch (err) {
            console.error('Sync Error Full:', err);
            const errMsg = err.response?.data?.message || err.message || 'Unknown error';
            const status = err.response?.status || 'No Status';
            setError(`Sync Failed (${status}): ${errMsg}. Please refresh.`);
        }
    };

    const handleFirebaseEmailAuth = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);

        try {
            let userCredential;
            if (mode === 'email-login') {
                userCredential = await signInWithEmailAndPassword(fbAuth, email, password);
            } else {
                userCredential = await createUserWithEmailAndPassword(fbAuth, email, password);
            }
            await handleSync(userCredential.user);
        } catch (err) {
            console.error('Email Auth Error:', err);
            let msg = 'Authentication failed.';
            if (err.code === 'auth/too-many-requests') msg = 'Too many attempts. Wait 15 minutes.';
            else if (err.code === 'auth/invalid-credential') msg = 'Invalid email or password.';
            else if (err.code === 'auth/user-not-found') msg = 'No account found.';
            else if (err.code === 'auth/email-already-in-use') msg = 'Email already registered.';
            else if (err.code === 'auth/weak-password') msg = 'Password must be 6+ characters.';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleSendOtp = async (e) => {
        e.preventDefault();
        if (cooldown > 0) return;

        if (!recaptchaReady) {
            setError('Please complete the robot verification first.');
            return;
        }

        setError('');
        setMessage('');
        setLoading(true);

        try {
            const result = await signInWithPhoneNumber(fbAuth, phoneNumber, window.recaptchaVerifier);
            setConfirmationResult(result);
            setMessage('OTP sent! Check your phone.');
            setCooldown(60);
        } catch (err) {
            console.error('Phone Auth Error:', err);
            let msg = err.message || 'Failed to send OTP.';
            if (err.code === 'auth/too-many-requests') msg = 'Too many requests. Wait 15-30 minutes.';
            else if (err.code === 'auth/invalid-phone-number') msg = 'Invalid phone number. Use +1234567890 format.';
            else if (err.code) msg = `Error: ${err.code} - ${err.message}`;
            setError(msg);

            // Reset reCAPTCHA for retry
            setRecaptchaReady(false);
            recaptchaInitialized.current = false;
            if (window.recaptchaVerifier) {
                try { window.recaptchaVerifier.clear(); } catch (e) { }
                window.recaptchaVerifier = null;
            }
            // Re-initialize after a delay
            setTimeout(() => initRecaptcha(), 500);
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const result = await confirmationResult.confirm(verificationCode);
            await handleSync(result.user);
        } catch (err) {
            console.error('OTP Error:', err);
            setError('Invalid or expired code.');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        if (cooldown > 0) return;

        setError('');
        setMessage('');
        setLoading(true);

        try {
            await sendPasswordResetEmail(fbAuth, email);
            setMessage('✅ Reset email sent! Check inbox & spam folder.');
            setCooldown(60);
        } catch (err) {
            console.error('Reset Error:', err);
            let msg = 'Failed to send reset email.';
            if (err.code === 'auth/too-many-requests') msg = 'Too many requests. Wait 15-30 minutes.';
            else if (err.code === 'auth/user-not-found') msg = 'No account found with this email.';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const switchMode = (newMode) => {
        // Clean up reCAPTCHA when switching away from phone
        if (mode === 'phone' && newMode !== 'phone') {
            if (window.recaptchaVerifier) {
                try { window.recaptchaVerifier.clear(); } catch (e) { }
                window.recaptchaVerifier = null;
            }
            recaptchaInitialized.current = false;
            setRecaptchaReady(false);
        }
        setMode(newMode);
        setError('');
        setMessage('');
        setConfirmationResult(null);
    };

    const renderTabs = () => (
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem' }}>
            <button
                onClick={() => switchMode('email-login')}
                style={{
                    background: 'none',
                    border: 'none',
                    color: (mode.startsWith('email') || mode === 'forgot-password') ? 'var(--primary)' : 'var(--text-muted)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    borderBottom: (mode.startsWith('email') || mode === 'forgot-password') ? '2px solid var(--primary)' : 'none',
                    padding: '0.5rem 0'
                }}
            >
                Email
            </button>
            <button
                onClick={() => switchMode('phone')}
                style={{
                    background: 'none',
                    border: 'none',
                    color: mode === 'phone' ? 'var(--primary)' : 'var(--text-muted)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    borderBottom: mode === 'phone' ? '2px solid var(--primary)' : 'none',
                    padding: '0.5rem 0'
                }}
            >
                Phone
            </button>
        </div>
    );

    return (
        <div className="card animate-fade" style={{ maxWidth: '400px', margin: '4rem auto' }}>
            <h2 style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
                {mode === 'forgot-password' ? 'Reset Password' : (mode === 'email-login' || mode === 'phone' ? 'Welcome Back' : 'Join Ringez')}
            </h2>
            <p style={{ textAlign: 'center', marginBottom: '1.5rem', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                {mode === 'forgot-password' ? 'Enter your email for reset link.' : 'Login to sync your calls and balance.'}
            </p>

            {!confirmationResult && mode !== 'forgot-password' && renderTabs()}

            {error && (
                <div style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: '#ef4444',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    marginBottom: '1rem',
                    textAlign: 'center',
                    fontSize: '0.875rem'
                }}>
                    {error}
                </div>
            )}

            {message && (
                <div style={{
                    background: 'rgba(34, 197, 94, 0.1)',
                    color: '#22c55e',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    marginBottom: '1rem',
                    textAlign: 'center',
                    fontSize: '0.875rem'
                }}>
                    {message}
                </div>
            )}

            {/* Email Login/Register */}
            {(mode === 'email-login' || mode === 'email-register') && (
                <form onSubmit={handleFirebaseEmailAuth}>
                    <div style={{ position: 'relative', marginBottom: '1rem' }}>
                        <FiMail style={{ position: 'absolute', left: '12px', top: '14px', color: 'var(--text-muted)' }} />
                        <input
                            type="email"
                            placeholder="your@email.com"
                            style={{ paddingLeft: '2.5rem' }}
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            required
                        />
                    </div>
                    <div style={{ position: 'relative', marginBottom: '1.5rem' }}>
                        <FiLock style={{ position: 'absolute', left: '12px', top: '14px', color: 'var(--text-muted)' }} />
                        <input
                            type={showPassword ? "text" : "password"}
                            placeholder="Password (min 6 chars)"
                            style={{ paddingLeft: '2.5rem', paddingRight: '2.5rem' }}
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            required
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                                position: 'absolute',
                                right: '12px',
                                top: '14px',
                                background: 'none',
                                border: 'none',
                                color: 'var(--text-muted)',
                                cursor: 'pointer',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            {showPassword ? <FiEyeOff /> : <FiEye />}
                        </button>
                    </div>
                    {mode === 'email-login' && (
                        <div style={{ textAlign: 'right', marginBottom: '1rem' }}>
                            <button
                                type="button"
                                onClick={() => switchMode('forgot-password')}
                                style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 600 }}
                            >
                                Forgot Password?
                            </button>
                        </div>
                    )}
                    <button className="primary" style={{ width: '100%', height: '48px' }} disabled={loading}>
                        {loading ? 'Processing...' : (mode === 'email-login' ? 'Sign In' : 'Create Account')}
                    </button>
                    <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.875rem' }}>
                        {mode === 'email-login' ? "New user? " : "Already registered? "}
                        <button
                            type="button"
                            onClick={() => switchMode(mode === 'email-login' ? 'email-register' : 'email-login')}
                            style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 600, cursor: 'pointer' }}
                        >
                            {mode === 'email-login' ? 'Sign Up' : 'Log In'}
                        </button>
                    </p>
                </form>
            )}

            {/* Phone Login */}
            {mode === 'phone' && (
                !confirmationResult ? (
                    <form onSubmit={handleSendOtp}>
                        <div style={{ position: 'relative', marginBottom: '1rem' }}>
                            <FiPhone style={{ position: 'absolute', left: '12px', top: '14px', color: 'var(--text-muted)' }} />
                            <input
                                type="tel"
                                placeholder="+1234567890"
                                style={{ paddingLeft: '2.5rem' }}
                                value={phoneNumber}
                                onChange={e => setPhoneNumber(e.target.value)}
                                required
                            />
                        </div>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '1rem', textAlign: 'center' }}>
                            Include country code (e.g., +1 for USA, +91 for India)
                        </p>

                        {/* reCAPTCHA Container */}
                        <div
                            ref={recaptchaContainerRef}
                            style={{
                                marginBottom: '1.5rem',
                                display: 'flex',
                                justifyContent: 'center',
                                minHeight: '78px'
                            }}
                        ></div>

                        <button
                            className="primary"
                            style={{ width: '100%', height: '48px' }}
                            disabled={loading || cooldown > 0 || !recaptchaReady}
                        >
                            {loading ? 'Sending OTP...' : (cooldown > 0 ? `Wait ${cooldown}s` : (recaptchaReady ? 'Send OTP' : 'Complete verification above'))}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleVerifyOtp}>
                        <div style={{ position: 'relative', marginBottom: '1rem' }}>
                            <FiLock style={{ position: 'absolute', left: '12px', top: '14px', color: 'var(--text-muted)' }} />
                            <input
                                type="text"
                                placeholder="6-digit code"
                                style={{ paddingLeft: '2.5rem' }}
                                value={verificationCode}
                                onChange={e => setVerificationCode(e.target.value)}
                                required
                            />
                        </div>
                        <button className="primary" style={{ width: '100%', height: '48px' }} disabled={loading}>
                            {loading ? 'Verifying...' : 'Verify & Login'}
                        </button>
                        <button
                            type="button"
                            onClick={() => { setConfirmationResult(null); setVerificationCode(''); setError(''); }}
                            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', width: '100%', marginTop: '1rem', cursor: 'pointer' }}
                        >
                            Use different number
                        </button>
                    </form>
                )
            )}

            {/* Forgot Password */}
            {mode === 'forgot-password' && (
                <form onSubmit={handleForgotPassword}>
                    <div style={{ position: 'relative', marginBottom: '1.5rem' }}>
                        <FiMail style={{ position: 'absolute', left: '12px', top: '14px', color: 'var(--text-muted)' }} />
                        <input
                            type="email"
                            placeholder="your@email.com"
                            style={{ paddingLeft: '2.5rem' }}
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            required
                        />
                    </div>
                    <button
                        className="primary"
                        style={{ width: '100%', height: '48px' }}
                        disabled={loading || cooldown > 0}
                    >
                        {loading ? 'Sending...' : (cooldown > 0 ? `Wait ${cooldown}s` : 'Send Reset Link')}
                    </button>
                    <button
                        type="button"
                        onClick={() => switchMode('email-login')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            background: 'none',
                            border: 'none',
                            color: 'var(--text-muted)',
                            width: '100%',
                            marginTop: '1.5rem',
                            cursor: 'pointer',
                            fontWeight: 600
                        }}
                    >
                        <FiArrowLeft size={14} /> Back to Login
                    </button>
                </form>
            )}
        </div>
    );
};

export default Auth;
